/**
 * Einheitliche Bargeld- / Zahlungsfluss-Berechnung.
 * Modell: verfügbar = Eingänge − Ausgänge (Bar bzw. elektronisch getrennt).
 */

import {
  getWorkerSessionRegisterOutflow,
  getWorkerSessionExternalOutflow,
  getManagerRegisterOutflow,
  getManagerExternalOutflow,
  getGeneralRegisterOutflow,
  getGeneralExternalOutflow,
  isSetupPhase,
  resolveLiquidOutflowPortions
} from './fundingSource';
import { getCashRegisterOutflowContextForBusiness } from './cashRegisterOutflowContext';
import { sumElectronicSalesByProvider } from './saleSessionHelpers';
import { loadSessionsForWorker } from './workerSessionsStorage';
import { readExpensesSyncForBusiness } from './expensesReadSource';
import { readArrayFieldFromMergedBlob } from './businessStateSync';
import { resolveSaleUnpaidAmount } from '../domain/customers/balance/saleBalance';
import { resolveSaleRegisterIncome } from '../domain/sales/checkout/paymentAllocation';
import { getSaleTotalAmount } from '../domain/restaurant/restaurantDishSaleService';
import { collectCustomerDebtPayments } from '../domain/customers/balance/debtCollection';
import { computeLiquidBalanceByMethod } from './liquidBalanceByChannel';

const CASH_KEYS = new Set(['cash', 'espèces', 'especes']);
const GENERIC_ELECTRONIC_KEYS = new Set(['electronic', 'électronique', 'card', 'mobile', 'bank', 'transfer']);
const CREDIT_KEYS = new Set(['credit', 'credit_payment', 'on_account']);

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const CASH_REGISTER_OUTFLOW_TYPES = new Set([
  'expense',
  'purchase',
  'supplier_payment',
  'cash_out'
]);

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Behält negative Werte (z. B. negatives Attendu); nur NaN → fallback. */
export function toFiniteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Ausgangs-Beträge sind immer positive Magnitude vor Subtraktion. */
export function outflowMagnitude(value) {
  return Math.abs(toFiniteNumber(value, 0));
}

function entryDateMs(entry) {
  const createdRaw = entry?.createdAt || entry?.timestamp;
  const dateRaw = entry?.date || entry?.paymentDate;

  // Dépenses: date picker = YYYY-MM-DD only → use createdAt for session window
  if (dateRaw && DATE_ONLY_RE.test(String(dateRaw).trim()) && createdRaw) {
    const createdMs = new Date(createdRaw).getTime();
    if (Number.isFinite(createdMs)) return createdMs;
  }

  const raw = dateRaw || createdRaw;
  if (!raw) return null;

  if (DATE_ONLY_RE.test(String(raw).trim())) {
    const [y, m, d] = String(raw).trim().split('-').map(Number);
    const localEnd = new Date(y, m - 1, d, 23, 59, 59, 999);
    return localEnd.getTime();
  }

  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** @returns {'cash'|'electronic'|'credit'} */
export function getPaymentBucket(entry = {}) {
  const paymentMethod = String(entry?.paymentMethod || '').trim().toLowerCase();
  const provider = String(entry?.electronicPaymentMethod || entry?.payment?.paymentMethod || '').trim().toLowerCase();
  // Prefer void-aware sale total; falls through to amount fields for expenses/payments
  const total =
    getSaleTotalAmount(entry) ||
    toNumber(entry?.total ?? entry?.totalAmount ?? entry?.amount ?? entry?.revenue);
  // Sales: Domain unpaid SSOT; other entries keep legacy unpaidAmount/creditAmount
  const unpaid = Math.max(
    resolveSaleUnpaidAmount(entry),
    toNumber(entry?.unpaidAmount ?? entry?.creditAmount)
  );

  if (CREDIT_KEYS.has(paymentMethod)) return 'credit';
  // Pure créance (no paid channel left)
  if (unpaid > 0 && (total <= 0 || unpaid >= total - 0.0001)) return 'credit';

  if (CASH_KEYS.has(paymentMethod)) return 'cash';
  if (provider && !CASH_KEYS.has(provider) && !CREDIT_KEYS.has(provider)) return 'electronic';
  if (
    !paymentMethod ||
    paymentMethod === 'electronic' ||
    paymentMethod === 'électronique' ||
    paymentMethod === 'card' ||
    paymentMethod === 'mobile'
  ) {
    return 'electronic';
  }
  return paymentMethod === 'cash' ? 'cash' : 'electronic';
}

export function isCashPaymentMethod(paymentMethod, electronicPaymentMethod) {
  return getPaymentBucket({ paymentMethod, electronicPaymentMethod }) === 'cash';
}

export function isExpensePaymentMirror(payment) {
  return payment?.transactionType === 'expense_payment';
}

export function filterEntriesInSessionWindow(entries, loginTime, logoutTime = null) {
  if (!loginTime) return [];
  const start = new Date(loginTime).getTime();
  const end = logoutTime ? new Date(logoutTime).getTime() : Date.now();
  if (!Number.isFinite(start)) return [];

  return (Array.isArray(entries) ? entries : []).filter((entry) => {
    const ms = entryDateMs(entry);
    if (ms == null) return false;
    return ms >= start && ms <= end;
  });
}

/**
 * Gesamtliquidität des Unternehmens (Bilanz / Zahlungsrapport).
 * Kein Schicht-Anfangsbestand — nur kumulierte Zahlungsflüsse.
 *
 * @deprecated ADR-0018 — do not add production call sites.
 * Prefer `domain/finance/cashflow` → `computeLiquidBalanceByMethod` /
 * `resolveBilanLiquidBalances`. This is a compatibility shape over the Method-Liquid owner.
 */
export function computeBusinessLiquidBalance({
  sales = [],
  expenses = [],
  supermarketPayments = [],
  invoicePayments = [],
  customerDebts = null,
  customerDebtPayments = [],
  installationPhase = null,
  allSales = null
} = {}) {
  // Owner formula only — flat shape for tests / legacy callers
  const forceOpening =
    installationPhase != null
      ? Boolean(installationPhase)
      : isSetupPhase({ sales: allSales != null ? allSales : sales });

  const debtPayments =
    Array.isArray(customerDebtPayments) && customerDebtPayments.length > 0
      ? customerDebtPayments
      : null;

  const liquid = computeLiquidBalanceByMethod({
    sales,
    expenses,
    supermarketPayments,
    invoicePayments,
    customerDebts: debtPayments == null ? customerDebts : null,
    customerDebtPayments: debtPayments,
    installationPhase: forceOpening
  });

  return {
    cash: toNumber(liquid?.cash?.balance),
    electronic: toNumber(liquid?.electronic?.balance),
    total: toNumber(liquid?.total?.balance),
    incoming: {
      cash: toNumber(liquid?.cash?.income),
      electronic: toNumber(liquid?.electronic?.income),
      total: toNumber(liquid?.cash?.income) + toNumber(liquid?.electronic?.income)
    },
    outgoing: {
      cash: toNumber(liquid?.cash?.outgoing),
      electronic: toNumber(liquid?.electronic?.outgoing),
      total: toNumber(liquid?.cash?.outgoing) + toNumber(liquid?.electronic?.outgoing)
    },
    liquid
  };
}

const BOOKABLE_REGISTER_OUTFLOW_TYPES = new Set(['expense', 'purchase', 'supplier_payment']);

function registerOutflowInWindow(t, start, end, sessionWorkerId) {
  if (!t || !CASH_REGISTER_OUTFLOW_TYPES.has(String(t.type || ''))) return false;
  if (t.voided === true) return false;
  const amount = toNumber(t.amount);
  if (amount <= 0) return false;

  const sid = sessionWorkerId != null ? String(sessionWorkerId).trim() : null;
  if (sid) {
    const role = String(t.actorRole || '').toLowerCase();
    const wid = String(t.workerId || '').trim().toLowerCase();
    if (role === 'manager' || wid === 'manager' || wid.startsWith('manager_')) return false;
    if (wid && wid !== String(sessionWorkerId).trim().toLowerCase()) return false;
  }

  if (start != null && Number.isFinite(start)) {
    const ts = t.timestamp || t.createdAt;
    if (ts) {
      const ms = new Date(ts).getTime();
      if (!Number.isFinite(ms) || ms < start || ms > end) return false;
    }
  }
  return true;
}

/**
 * Bar-Journal-Ausgänge nach Art splitten.
 * - bookable: expense/purchase/supplier_payment (spiegeln oft Buchhaltung → max mit flow)
 * - cashOutOnly: reines cash_out (nie in Accounting → immer additiv)
 */
export function sumRegisterCashOutflowsByKind(
  registerSession,
  loginTime,
  logoutTime = null,
  sessionWorkerId = null
) {
  const empty = { bookable: 0, cashOutOnly: 0, total: 0 };
  if (!registerSession || !Array.isArray(registerSession.transactions)) return empty;

  const start = loginTime ? new Date(loginTime).getTime() : null;
  const end = logoutTime ? new Date(logoutTime).getTime() : Date.now();

  let bookable = 0;
  let cashOutOnly = 0;
  for (const t of registerSession.transactions) {
    if (!registerOutflowInWindow(t, start, end, sessionWorkerId)) continue;
    const amount = toNumber(t.amount);
    const type = String(t.type || '');
    if (type === 'cash_out') cashOutOnly += amount;
    else if (BOOKABLE_REGISTER_OUTFLOW_TYPES.has(type)) bookable += amount;
    else cashOutOnly += amount;
  }
  return { bookable, cashOutOnly, total: bookable + cashOutOnly };
}

/** Bar-Ausgänge aus Kassen-Transaktionen (recordCashOutflow) innerhalb der Schicht. */
export function sumRegisterCashOutflows(registerSession, loginTime, logoutTime = null, sessionWorkerId = null) {
  return sumRegisterCashOutflowsByKind(registerSession, loginTime, logoutTime, sessionWorkerId).total;
}

/** Alle Bar-Abgänge im Kassenjournal (Manager + Kassierer) — Istkasse. */
export function sumGeneralRegisterCashOutflows(registerSession, loginTime, logoutTime = null) {
  return sumRegisterCashOutflows(registerSession, loginTime, logoutTime, null);
}

/**
 * Robuste Schicht-Ausgänge: Dépenses/Achats (Buchhaltung ∪ Journal) + reines cash_out additiv.
 * bookable: max(flow, journal) vermeidet Doppelzählung von gespiegelten Posten.
 * cash_out: nur im Journal → immer zusätzlich (sonst Lücke).
 */
export function resolveSessionOutflows(sessionFlow, registerSession, loginTime, logoutTime = null, sessionWorkerId = null) {
  const journal = sumRegisterCashOutflowsByKind(
    registerSession,
    loginTime,
    logoutTime,
    sessionWorkerId
  );
  const flowCash = outflowMagnitude(sessionFlow?.cashOutgoing);
  return {
    cashOutgoing: Math.max(flowCash, journal.bookable) + journal.cashOutOnly,
    electronicOutgoing: outflowMagnitude(sessionFlow?.electronicOutgoing),
    journalBookable: journal.bookable,
    journalCashOutOnly: journal.cashOutOnly
  };
}

/**
 * Liquidität während einer Worker-Schicht (Écart / Kassen-Soll).
 * Formel: Schicht-Anfang + Bar-Einnahmen − Bar-Ausgänge.
 * Setup/Opening-Capital: gleiche Remapping-Regel wie computeLiquidBalanceByMethod.
 */
export function computeSessionLiquidBalance({
  initialCash = 0,
  initialElectronic = 0,
  sales = [],
  expenses = [],
  supermarketPayments = [],
  invoicePayments = [],
  customerDebtPayments = [],
  customerDebts = null,
  loginTime = null,
  logoutTime = null,
  sessionWorkerId = null,
  installationPhase = null,
  allSales = null
} = {}) {
  const forceOpening =
    installationPhase != null
      ? Boolean(installationPhase)
      : isSetupPhase({ sales: allSales != null ? allSales : sales });
  const portionOpts = { installationPhase: forceOpening };

  const sessionSales = loginTime
    ? filterEntriesInSessionWindow(sales, loginTime, logoutTime)
    : (Array.isArray(sales) ? sales : []);

  const sessionExpenses = loginTime
    ? filterEntriesInSessionWindow(expenses, loginTime, logoutTime)
    : (Array.isArray(expenses) ? expenses : []);

  const sessionPayments = loginTime
    ? filterEntriesInSessionWindow(
        [
          ...(Array.isArray(supermarketPayments) ? supermarketPayments : []),
          ...(Array.isArray(invoicePayments) ? invoicePayments : [])
        ].filter((p) => !isExpensePaymentMirror(p)),
        loginTime,
        logoutTime
      )
    : [
        ...(Array.isArray(supermarketPayments) ? supermarketPayments : []),
        ...(Array.isArray(invoicePayments) ? invoicePayments : [])
      ].filter((p) => !isExpensePaymentMirror(p));

  // Créance-Einzug (void-aware via collectCustomerDebtPayments)
  let debtPaymentEntries = Array.isArray(customerDebtPayments) ? customerDebtPayments : [];
  if ((!debtPaymentEntries.length) && Array.isArray(customerDebts)) {
    debtPaymentEntries = collectCustomerDebtPayments(customerDebts);
  }
  const sessionDebtPayments = (() => {
    const inWindow = loginTime
      ? filterEntriesInSessionWindow(debtPaymentEntries, loginTime, logoutTime)
      : debtPaymentEntries;
    if (!sessionWorkerId) return inWindow;
    const sid = String(sessionWorkerId).trim();
    return inWindow.filter((p) => {
      const wid = String(p?.workerId ?? '').trim();
      // Peer collections must not inflate another cashier's Attendu
      return wid && wid === sid;
    });
  })();

  let cashSales = 0;
  let electronicSales = 0;
  let cashDebtCollections = 0;
  let electronicDebtCollections = 0;

  sessionSales.forEach((sale) => {
    const { amount: paid, channel } = resolveSaleRegisterIncome(sale);
    if (paid <= 0) return; // pure credit / cancelled
    if (channel === 'cash') cashSales += paid;
    else if (channel === 'electronic') electronicSales += paid;
  });

  sessionDebtPayments.forEach((entry) => {
    const amount = toNumber(entry.amount ?? entry.totalAmount);
    if (amount <= 0) return;
    if (getPaymentBucket(entry) === 'cash') cashDebtCollections += amount;
    else electronicDebtCollections += amount;
  });

  const sid = sessionWorkerId != null ? String(sessionWorkerId).trim() : null;

  let cashPaymentsOutgoing = 0;
  let electronicPaymentsOutgoing = 0;
  let cashExpensesOutgoing = 0;
  let electronicExpensesOutgoing = 0;
  let cashPaymentsExternal = 0;
  let electronicPaymentsExternal = 0;
  let cashExpensesExternal = 0;
  let electronicExpensesExternal = 0;
  let managerCashOutgoing = 0;
  let managerElectronicOutgoing = 0;
  let managerCashExternal = 0;
  let managerElectronicExternal = 0;
  let generalCashOutgoing = 0;
  let generalElectronicOutgoing = 0;

  const applyWorkerOutflow = (entry, registerAmt, externalAmt) => {
    if (registerAmt <= 0 && externalAmt <= 0) return;
    if (getPaymentBucket(entry) === 'cash') {
      cashPaymentsOutgoing += registerAmt;
      cashPaymentsExternal += externalAmt;
    } else {
      electronicPaymentsOutgoing += registerAmt;
      electronicPaymentsExternal += externalAmt;
    }
  };

  const applyWorkerExpenseOutflow = (entry, registerAmt, externalAmt) => {
    if (registerAmt <= 0 && externalAmt <= 0) return;
    if (getPaymentBucket(entry) === 'cash') {
      cashExpensesOutgoing += registerAmt;
      cashExpensesExternal += externalAmt;
    } else {
      electronicExpensesOutgoing += registerAmt;
      electronicExpensesExternal += externalAmt;
    }
  };

  const applyManagerOutflow = (entry) => {
    const reg = getManagerRegisterOutflow(entry, portionOpts);
    const ext = getManagerExternalOutflow(entry, portionOpts);
    if (getPaymentBucket(entry) === 'cash') {
      managerCashOutgoing += reg;
      managerCashExternal += ext;
    } else {
      managerElectronicOutgoing += reg;
      managerElectronicExternal += ext;
    }
  };

  const applyGeneralOutflow = (entry) => {
    const reg = getGeneralRegisterOutflow(entry, portionOpts);
    if (getPaymentBucket(entry) === 'cash') generalCashOutgoing += reg;
    else generalElectronicOutgoing += reg;
  };

  sessionPayments.forEach((entry) => {
    if (sid) {
      applyWorkerOutflow(
        entry,
        getWorkerSessionRegisterOutflow(entry, sid, portionOpts),
        getWorkerSessionExternalOutflow(entry, sid, portionOpts)
      );
      applyManagerOutflow(entry);
      applyGeneralOutflow(entry);
    } else {
      const { registerAmt, externalAmt } = resolveLiquidOutflowPortions(entry, portionOpts);
      applyWorkerOutflow(entry, registerAmt, externalAmt);
      generalCashOutgoing += getPaymentBucket(entry) === 'cash' ? registerAmt : 0;
      generalElectronicOutgoing += getPaymentBucket(entry) === 'cash' ? 0 : registerAmt;
    }
  });

  sessionExpenses.forEach((entry) => {
    if (sid) {
      applyWorkerExpenseOutflow(
        entry,
        getWorkerSessionRegisterOutflow(entry, sid, portionOpts),
        getWorkerSessionExternalOutflow(entry, sid, portionOpts)
      );
      applyManagerOutflow(entry);
      applyGeneralOutflow(entry);
    } else {
      const { registerAmt, externalAmt } = resolveLiquidOutflowPortions(entry, portionOpts);
      applyWorkerExpenseOutflow(entry, registerAmt, externalAmt);
      generalCashOutgoing += getPaymentBucket(entry) === 'cash' ? registerAmt : 0;
      generalElectronicOutgoing += getPaymentBucket(entry) === 'cash' ? 0 : registerAmt;
    }
  });

  const cashOutgoing = cashPaymentsOutgoing + cashExpensesOutgoing;
  const electronicOutgoing = electronicPaymentsOutgoing + electronicExpensesOutgoing;
  const cashExternalOutgoing = cashPaymentsExternal + cashExpensesExternal;
  const electronicExternalOutgoing = electronicPaymentsExternal + electronicExpensesExternal;

  const cashInTotal = cashSales + cashDebtCollections;
  const electronicInTotal = electronicSales + electronicDebtCollections;

  return {
    cash: toNumber(initialCash) + cashInTotal - cashOutgoing,
    electronic: toNumber(initialElectronic) + electronicInTotal - electronicOutgoing,
    cashSales,
    electronicSales,
    cashDebtCollections,
    electronicDebtCollections,
    cashOutgoing,
    electronicOutgoing,
    cashExternalOutgoing,
    electronicExternalOutgoing,
    managerCashOutgoing,
    managerElectronicOutgoing,
    managerCashExternal,
    managerElectronicExternal,
    generalCashOutgoing,
    generalElectronicOutgoing,
    cashPaymentsOutgoing,
    cashExpensesOutgoing,
    electronicPaymentsOutgoing,
    electronicExpensesOutgoing,
    cashPaymentsExternal,
    cashExpensesExternal,
    electronicPaymentsExternal,
    electronicExpensesExternal,
    outgoingBreakdown: {
      payments: {
        cash: cashPaymentsOutgoing,
        electronic: electronicPaymentsOutgoing,
        total: cashPaymentsOutgoing + electronicPaymentsOutgoing,
        cashExternal: cashPaymentsExternal,
        electronicExternal: electronicPaymentsExternal
      },
      expenses: {
        cash: cashExpensesOutgoing,
        electronic: electronicExpensesOutgoing,
        total: cashExpensesOutgoing + electronicExpensesOutgoing,
        cashExternal: cashExpensesExternal,
        electronicExternal: electronicExpensesExternal
      },
      external: {
        cash: cashExternalOutgoing,
        electronic: electronicExternalOutgoing,
        total: cashExternalOutgoing + electronicExternalOutgoing
      }
    },
    installationPhase: forceOpening,
    expectedCash: toNumber(initialCash) + cashInTotal - cashOutgoing,
    expectedElectronic: toNumber(initialElectronic) + electronicInTotal - electronicOutgoing
  };
}

/**
 * Kassen-Session zu Worker-Session zuordnen (Journal für Ausgänge).
 */
export function findRegisterSessionForWorkerSession(
  workerSession,
  registerSessions = [],
  activeRegisterSession = null
) {
  if (!workerSession?.loginTime) return null;

  const workerId = String(workerSession.workerId ?? '').trim();
  const loginMs = new Date(workerSession.loginTime).getTime();
  if (!workerId || !Number.isFinite(loginMs)) return null;

  const candidates = [];
  if (activeRegisterSession && String(activeRegisterSession.workerId ?? '').trim() === workerId) {
    candidates.push(activeRegisterSession);
  }
  for (const registerSession of registerSessions || []) {
    if (!registerSession) continue;
    if (String(registerSession.workerId ?? '').trim() !== workerId) continue;
    candidates.push(registerSession);
  }

  let best = null;
  let bestDelta = Infinity;
  for (const registerSession of candidates) {
    const registerLoginMs = new Date(registerSession.loginTime).getTime();
    if (!Number.isFinite(registerLoginMs)) continue;
    const delta = Math.abs(registerLoginMs - loginMs);
    if (delta <= 5 * 60 * 1000 && delta < bestDelta) {
      bestDelta = delta;
      best = registerSession;
    }
  }
  return best;
}

function applyRegisterOutflowsToSessionFlow(session, sessionFlow, registerSession) {
  if (!sessionFlow || !registerSession) return sessionFlow;

  const sessionWorkerId = session?.workerId ?? null;
  const { cashOutgoing, electronicOutgoing } = resolveSessionOutflows(
    sessionFlow,
    registerSession,
    session?.loginTime,
    session?.logoutTime,
    sessionWorkerId
  );
  const initialCash = Number(session?.initialAmount ?? session?.cashAmount) || 0;
  const initialElectronic =
    Number(session?.initialElectronicAmount ?? session?.electronicAmount) || 0;
  const cashIn =
    outflowMagnitude(sessionFlow.cashSales) + outflowMagnitude(sessionFlow.cashDebtCollections);
  const electronicIn =
    outflowMagnitude(sessionFlow.electronicSales) +
    outflowMagnitude(sessionFlow.electronicDebtCollections);
  const expectedCash = initialCash + cashIn - cashOutgoing;
  const expectedElectronic = initialElectronic + electronicIn - electronicOutgoing;

  return {
    ...sessionFlow,
    cashOutgoing,
    electronicOutgoing,
    cash: expectedCash,
    electronic: expectedElectronic,
    expectedCash,
    expectedElectronic
  };
}

/**
 * Soll-Beträge für eine Worker-Session (Settings, Session-Manager, Worker-Dashboard).
 * Nutzt dasselbe Ausgangs-Modell wie beim Logout (Buchhaltung + Kassenjournal).
 * Setup-Erkennung nutzt Geschäfts-Sales (allSales), nicht nur session.sales.
 */
export function computeSessionExpectedFromWorkerSession(session, {
  expenses = [],
  supermarketPayments = [],
  invoicePayments = [],
  customerDebts = null,
  customerDebtPayments = [],
  registerSessions = null,
  activeRegisterSession = null,
  registerSession = null,
  installationPhase = null,
  allSales = null
} = {}) {
  let resolvedAllSales = allSales;
  let resolvedCustomerDebts = customerDebts;
  if (resolvedAllSales == null || resolvedCustomerDebts == null) {
    const bid = session?.businessId;
    if (bid) {
      try {
        if (resolvedAllSales == null) {
          resolvedAllSales = readArrayFieldFromMergedBlob(bid, 'sales') || [];
        }
        if (resolvedCustomerDebts == null) {
          resolvedCustomerDebts = readArrayFieldFromMergedBlob(bid, 'customerDebts') || [];
        }
      } catch {
        if (resolvedAllSales == null) resolvedAllSales = null;
        if (resolvedCustomerDebts == null) resolvedCustomerDebts = null;
      }
    }
  }

  const baseFlow = computeSessionLiquidBalance({
    initialCash: Number(session?.initialAmount ?? session?.cashAmount) || 0,
    initialElectronic: Number(session?.initialElectronicAmount ?? session?.electronicAmount) || 0,
    sales: Array.isArray(session?.sales) ? session.sales : [],
    expenses,
    supermarketPayments,
    invoicePayments,
    customerDebts: resolvedCustomerDebts,
    customerDebtPayments,
    loginTime: session?.loginTime ?? null,
    logoutTime: session?.logoutTime ?? null,
    sessionWorkerId: session?.workerId ?? null,
    installationPhase,
    allSales: resolvedAllSales
  });

  const resolvedRegisterSession =
    registerSession ??
    findRegisterSessionForWorkerSession(
      session,
      Array.isArray(registerSessions) ? registerSessions : [],
      activeRegisterSession
    );

  return applyRegisterOutflowsToSessionFlow(session, baseFlow, resolvedRegisterSession);
}

/** Payload für CashExpectedBreakdown aus Session + Flow-Daten. */
export function sessionFlowToBreakdownDeviation(session, sessionFlow) {
  return {
    initialAmount: Number(session?.initialAmount ?? session?.cashAmount) || 0,
    initialElectronicAmount: Number(session?.initialElectronicAmount ?? session?.electronicAmount) || 0,
    cashSales: sessionFlow.cashSales,
    electronicSales: sessionFlow.electronicSales,
    cashDebtCollections: sessionFlow.cashDebtCollections ?? 0,
    electronicDebtCollections: sessionFlow.electronicDebtCollections ?? 0,
    cashOutgoing: sessionFlow.cashOutgoing,
    electronicOutgoing: sessionFlow.electronicOutgoing,
    expectedAmount: sessionFlow.expectedCash,
    expectedElectronicAmount: sessionFlow.expectedElectronic
  };
}

/** Vollständiger Écart-Payload — identisch zum Logout (CashRegisterExpectedDetailTable). */
export function buildSessionDeviationDetail(session, sessionFlow) {
  if (!session || !sessionFlow) return null;

  const initialAmount = Number(session?.initialAmount ?? session?.cashAmount) || 0;
  const initialElectronicAmount =
    Number(session?.initialElectronicAmount ?? session?.electronicAmount) || 0;
  const finalCash =
    Number(
      session?.finalCashAmount ??
        (session?.logoutTime ? session?.cashAmount : session?.initialAmount)
    ) || 0;
  const finalElectronic =
    session?.finalElectronicAmount != null
      ? Number(session.finalElectronicAmount) || 0
      : Number(session?.electronicAmount) || 0;
  const breakdown = sessionFlow.outgoingBreakdown || {};
  const sales = Array.isArray(session?.sales) ? session.sales : [];
  const expectedCash = sessionFlow.expectedCash;
  const expectedElectronic = sessionFlow.expectedElectronic;

  return {
    initialAmount,
    initialElectronicAmount,
    cashSales: sessionFlow.cashSales,
    electronicSales: sessionFlow.electronicSales,
    cashDebtCollections: sessionFlow.cashDebtCollections ?? 0,
    electronicDebtCollections: sessionFlow.electronicDebtCollections ?? 0,
    electronicSalesByProvider: sumElectronicSalesByProvider(sales),
    cashOutgoing: sessionFlow.cashOutgoing,
    electronicOutgoing: sessionFlow.electronicOutgoing,
    cashPaymentsOutgoing: sessionFlow.cashPaymentsOutgoing ?? breakdown.payments?.cash ?? 0,
    cashExpensesOutgoing: sessionFlow.cashExpensesOutgoing ?? breakdown.expenses?.cash ?? 0,
    electronicPaymentsOutgoing:
      sessionFlow.electronicPaymentsOutgoing ?? breakdown.payments?.electronic ?? 0,
    electronicExpensesOutgoing:
      sessionFlow.electronicExpensesOutgoing ?? breakdown.expenses?.electronic ?? 0,
    cashExternalOutgoing: sessionFlow.cashExternalOutgoing ?? 0,
    electronicExternalOutgoing: sessionFlow.electronicExternalOutgoing ?? 0,
    managerCashOutgoing: sessionFlow.managerCashOutgoing ?? 0,
    managerElectronicOutgoing: sessionFlow.managerElectronicOutgoing ?? 0,
    expectedAmount: expectedCash,
    expectedElectronicAmount: expectedElectronic,
    actualAmount: finalCash,
    actualElectronicAmount: finalElectronic,
    finalAmount: finalCash,
    difference: finalCash - expectedCash,
    electronicDifference: finalElectronic - expectedElectronic,
    totalDifference: finalCash + finalElectronic - expectedCash - expectedElectronic
  };
}

/** Zentraler Écart für Logout / Session-Manager / Écart-Modal — eine einzige Quelle. */
export function resolveCentralSessionDeviation(session, businessId, {
  finalCashAmount = null,
  finalElectronicAmount = null,
  expenses = null,
  supermarketPayments = null,
  invoicePayments = null,
  customerDebts = null
} = {}) {
  if (!session) return null;

  const { registerSessions, activeRegisterSession } =
    getCashRegisterOutflowContextForBusiness(businessId);

  const sessionFlow = computeSessionExpectedFromWorkerSession(session, {
    expenses: expenses ?? [],
    supermarketPayments: supermarketPayments ?? [],
    invoicePayments: invoicePayments ?? [],
    customerDebts:
      customerDebts ??
      (businessId ? readArrayFieldFromMergedBlob(businessId, 'customerDebts') || [] : null),
    registerSessions,
    activeRegisterSession,
    allSales: businessId ? (readArrayFieldFromMergedBlob(businessId, 'sales') || []) : null
  });

  const sessionWithFinals = {
    ...session,
    ...(finalCashAmount != null ? { finalCashAmount: Number(finalCashAmount) || 0 } : {}),
    ...(finalElectronicAmount != null ? { finalElectronicAmount: Number(finalElectronicAmount) || 0 } : {})
  };

  return buildSessionDeviationDetail(sessionWithFinals, sessionFlow);
}

/** Recalcule l'écart enregistré via la session liée — même source que le Session-Manager. */
export function resolveCentralDeviationForStoredEcart(deviationRecord, businessId) {
  const nested = deviationRecord?.deviation;
  if (!nested || !businessId) return nested || null;

  const sessionId = deviationRecord?.sessionId;
  const workerId = deviationRecord?.workerId ?? deviationRecord?.previousWorkerId;
  if (!sessionId || !workerId) return nested;

  try {
    const sessions = loadSessionsForWorker(workerId, businessId) || [];
    const session = sessions.find(
      (candidate) =>
        String(candidate?.loginTime) === String(sessionId) ||
        String(candidate?.id) === String(sessionId)
    );
    if (!session?.logoutTime) return nested;

    const expenses = readExpensesSyncForBusiness(businessId) || [];
    const supermarketPayments = readArrayFieldFromMergedBlob(businessId, 'supermarketPayments') || [];
    const invoicePayments = readArrayFieldFromMergedBlob(businessId, 'invoicePayments') || [];
    const customerDebts = readArrayFieldFromMergedBlob(businessId, 'customerDebts') || [];

    return (
      resolveCentralSessionDeviation(session, businessId, {
        expenses,
        supermarketPayments,
        invoicePayments,
        customerDebts
      }) || nested
    );
  } catch {
    return nested;
  }
}

export {
  FUNDING_REGISTER,
  FUNDING_EXTERNAL,
  FUNDING_MIXED,
  isSetupPhase,
  resolveOutflowFunding,
  getRegisterOutflowAmount,
  getExternalOutflowAmount,
  attachFundingToOutflowRecord,
  isManagerOutflow,
  isAttributedToWorkerSession,
  getWorkerSessionRegisterOutflow,
  getManagerRegisterOutflow,
  getGeneralRegisterOutflow
} from './fundingSource';
